import { isHttpUrl, useMount } from "@amx/ux-twoui-utils";
import React, { createContext, useContext } from "react";
import { ExternalEntities, ExternalEntitiesProvider } from "../ExternalEntitiesProvider/ExternalEntitiesProvider";
import { TwoUIOperationsProvider } from "../TwoUIOperationProvider/TwoUIOperationProvider";

type LogComponentMountEventFunctionType = (twouiVersion: string, componentName: string, id?: string) => void;

export interface TwoUITelemetryIdsType {
    /**
     * Area or App ID used when logging events for telemetry.
     * This value can only be alphanumerics and hyphens, and has a max-length of 50 characters.
     */
    readonly areaBiId: string;
}

/**
 * Interface for TwoUITelemetryProvider
 */
interface TwoUITelemetryProviderProps {
    /**
     * Function used to log component mount events.
     */
    readonly logComponentMountEvent?: LogComponentMountEventFunctionType;

    /**
     * Triggered when we a HTTP link is used on component
     */
    readonly reportHttpUrl?: (url: string) => void;

    /**
     * Report user click.
     */
    readonly reportClick?: (biCustomClickParam: TwoUIBiCustomClick) => void;

    /**
     * Report component validation error event to find out wrong usage in production
     */
    readonly reportComponentValidationErrorEvent?: (componentName: string, errorMessage: string, errorCount: number) => void;

    /**
     * Function to get current CV id
     */
    readonly getCv?: () => string;

    /**
     * Telemetry IDs used when logging events for telemetry.
     */
    readonly twoUITelemetryIds: TwoUITelemetryIdsType;

    /**
     * External contexts
     */
    readonly externalEntities?: ExternalEntities;

    /**
     * Array of flights that are enabled.
     */
    readonly twoUIFlights: readonly string[];
}

/**
 * Defines custom click data for BI.
 *
 * ============= NOTICE =============
 * | This is a snapshot of interface BiCustomClick from
 * | core-client-telemetry and mean to expose to other partner of TwoUI. This is
 * | NOT meant to be in sync with core-client-telemetry interfaces.
 * ==========================================
 */
export interface TwoUIBiCustomClick {
    /** Name of an area of a page clicked element belongs to (known values: header, body, footer). */
    readonly pageArea: string;

    /** Optional name of a group of containers clicked element belongs to. */
    readonly containerGroup?: string;

    /** Name of a container (OR an element) within a group of containers clicked element belongs to. */
    readonly container?: string;

    /** Click (element) ID. This value will be used to identify the click in reports. */
    readonly clickId: string;

    /** Report target uri if this event will redirect to different page */
    readonly targetUri?: string;
}

/**
 * TwoUI telemetry type, providing all tool related to telemetry and TwoUI
 */
export interface TwoUITelemetryContextType {
    /**
     * Function used to log component mount events.
     */
    readonly logComponentMountEvent: LogComponentMountEventFunctionType;

    /**
     * Triggered when we a HTTP link is used on component
     */
    readonly checkUrlAndReportHttp: (url: string | undefined) => void;

    /**
     * Report component validation error event to find out wrong usage in production
     */
    readonly reportComponentValidationErrorEvent: (componentName: string, errorMessage: string, errorCount: number) => void;

    /**
     * Function to get current CV id
     */
    readonly getCv: () => string;

    /**
     * Report user click.
     */
    readonly reportClick: (click: TwoUIBiCustomClick) => void;

    /**
     * Telemetry IDs used when logging events for telemetry.
     */
    readonly twoUITelemetryIds: TwoUITelemetryIdsType;

    /**
     * Array of flights that are enabled.
     */
    readonly twoUIFlights: readonly string[];

}

export const DefaultTwoUITelemetryIds: TwoUITelemetryIdsType = {
    areaBiId: "areaUndefined"
};

const twoUITelemetryContext = createContext<TwoUITelemetryContextType>({
    reportComponentValidationErrorEvent: (_componentName: string, _errorMessage: string, _errorCount: number) => { return; },
    logComponentMountEvent: (_twouiVersion: string, _componentName: string, _id?: string) => { return; },
    twoUITelemetryIds: DefaultTwoUITelemetryIds,
    twoUIFlights: [],
    getCv: () => { return ""; },
    reportClick: (_biCustomClickParam: TwoUIBiCustomClick) => { return; },
    checkUrlAndReportHttp: (_url: string | undefined) => { return; }
});

export const useTwoUITelemetry = (): TwoUITelemetryContextType => {
    return useContext(twoUITelemetryContext);
};

export const useTwoUIFlights = (): readonly string[] => {
    const context = useContext(twoUITelemetryContext);
    return context.twoUIFlights;
};

export const useTwoUITelemetryIds = (): TwoUITelemetryIdsType => {
    const context = useContext(twoUITelemetryContext);

    return context.twoUITelemetryIds;
};

export const useComponentMountLogger: LogComponentMountEventFunctionType = (twouiVersion: string, componentName: string, id?: string) => {
    const context = useContext(twoUITelemetryContext);

    useMount(() => {
        context.logComponentMountEvent(twouiVersion, componentName, id);
    });
};

const sanitizeAreaBiId = (areaBiId: string): string => {
    const maxLength = 50;
    const sanitizedAreaBiId = areaBiId.replace(/[^a-z0-9\-]/gi, "").substring(0, maxLength);

    if (sanitizedAreaBiId.length === 0) {
        return DefaultTwoUITelemetryIds.areaBiId;
    } else {
        return sanitizedAreaBiId;
    }
};

const sanitizeTwoUITelemetryIds = (twoUITelemetryIds: TwoUITelemetryIdsType): TwoUITelemetryIdsType => {
    return {
        ...twoUITelemetryIds,
        areaBiId: sanitizeAreaBiId(twoUITelemetryIds.areaBiId)
    };
};

export let reportComponentValidationErrorEvent = (_componentName: string, _errorMessage: string, _errorCount: number) => { return; };

/**
 * Context provider for telemetry in TwoUI
 */
export const TwoUITelemetryProvider: React.FC<TwoUITelemetryProviderProps> = ({
    twoUITelemetryIds,
    twoUIFlights,
    logComponentMountEvent,
    reportHttpUrl: reportHttpLink,
    reportComponentValidationErrorEvent: externalReportComponentValidationErrorEvent,
    reportClick,
    getCv,
    children,
    externalEntities
}) => {
    const sanitizedTwoUITelemetryIds = sanitizeTwoUITelemetryIds(twoUITelemetryIds);

    const logComponentMountEventInternal = (twouiVersion: string, componentName: string, id?: string) => {
        logComponentMountEvent?.(twouiVersion, componentName, id);
    };

    const internalReportClick = (biCustomClickParam: TwoUIBiCustomClick) => {
        reportClick?.(biCustomClickParam);
    };

    const internalGetCv = (): string => {
        return getCv?.() || "";
    };

    reportComponentValidationErrorEvent = externalReportComponentValidationErrorEvent || reportComponentValidationErrorEvent;
    const internalReportComponentValidationErrorEvent = (componentName: string, errorMessage: string, errorCount: number) => {
        reportComponentValidationErrorEvent?.(componentName, errorMessage, errorCount);
    };

    const checkUrlAndReportHttp = (
        url: string | undefined
    ): void => {
        if (url && reportHttpLink && isHttpUrl(url)) {
            reportHttpLink(url);
        }
    };

    return (
        <twoUITelemetryContext.Provider
            value={{
                twoUITelemetryIds: sanitizedTwoUITelemetryIds,
                twoUIFlights: twoUIFlights,
                logComponentMountEvent: logComponentMountEventInternal,
                reportClick: internalReportClick,
                reportComponentValidationErrorEvent: internalReportComponentValidationErrorEvent,
                getCv: internalGetCv,
                checkUrlAndReportHttp
            }}
        >
            <ExternalEntitiesProvider
                externalEntities={externalEntities}
            >
                <TwoUIOperationsProvider>
                    {children}
                </TwoUIOperationsProvider>
            </ExternalEntitiesProvider>
        </twoUITelemetryContext.Provider>
    );
};
